import React, { useState, useRef, useMemo, useEffect } from "react";
import { fetchProtectedFile, getFileUrl, getFileFetchUrl } from "../utils/fileUrlHelper";
import AvatarImg from "../components/AvatarImg";
import SecureMediaViewer from "../components/SecureMediaViewer";
import RenewableFileImage from "../components/RenewableFileImage";
import MediaActionIcon, { MediaActionLoading } from "../components/MediaActionIcon";
import AsyncActionButton from "../components/AsyncActionButton";
import FormValidationNotice, { useFormValidation } from "../components/FormValidationNotice";
import { useLanguage } from "../i18n/LanguageContext";
import { appAlert, appConfirm } from "../components/AppDialog";
import { isAndroidPlatform } from "../utils/platformFeatures";
import { App as CapacitorApp } from "@capacitor/app";
import { androidDosyaPaylas } from "../utils/androidFileShare";

const ROL_SIRALAMASI = ["Kişisel","Baba","Anne","Çocuk","Amca","Hala","Teyze","Dayı","Dede","Nene","Diğer"];
const uyelerSirala = (liste) =>
  [...liste].sort((a,b) => {
    const ai = ROL_SIRALAMASI.indexOf(a.rol);
    const bi = ROL_SIRALAMASI.indexOf(b.rol);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

/* ─────────────────────────────────────────
   YARDIMCI FONKSİYONLAR & SABİTLER
───────────────────────────────────────── */
const KATEGORILER = [
  "Tümü",
  "🪪 Kimlik & Pasaport",
  "🏠 Tapu & Kira",
  "🚗 Araç",
  "🏥 Sağlık",
  "🛡️ Sigorta",
  "🎓 Eğitim",
  "👶 Çocuk Anısı",
  "👶 Çocuk Takibi",
  "🐾 Evcil Hayvan",
  "💳 Finans",
  "📄 Resmi Evrak",
  "🔧 Diğer",
];


const DOC_LABELS = {
  "Tümü":"All", "🪪 Kimlik & Pasaport":"🪪 ID & Passport", "🏠 Tapu & Kira":"🏠 Deed & Rent", "🚗 Araç":"🚗 Vehicle", "🏥 Sağlık":"🏥 Health", "🛡️ Sigorta":"🛡️ Insurance", "🎓 Eğitim":"🎓 Education", "👶 Çocuk Anısı":"👶 Child Memory", "👶 Çocuk Takibi":"👶 Child Tracking", "🐾 Evcil Hayvan":"🐾 Pet", "💳 Finans":"💳 Finance", "📄 Resmi Evrak":"📄 Official Document", "🔧 Diğer":"🔧 Other",
  "Belge Yükle":"Upload Document", "Dosya seçmek için dokun":"Tap to select files", "Maks":"Max", "Belge adı *":"Document name *", "Açıklama (isteğe bağlı)":"Description (optional)", "Kategori":"Category", "Geçerlilik Tarihi":"Validity Date", "Kimler görebilir?":"Who can view?", "yükleniyor...":"uploading...", "Yükle":"Upload", "Temizle":"Clear",
  "1 veya 2 dosya seçin":"Select 1 or 2 files", "İkinci dosyayı ekle":"Add the second file", "En fazla 2 dosya":"Up to 2 files", "Dosya başına maksimum {{max}} MB":"Maximum {{max}} MB per file", "Seçilen dosyalar":"Selected files", "{{count}} dosya seçildi":"{{count}} files selected", "En fazla 2 dosya seçebilirsiniz.":"You can select up to 2 files.", "{{name}} dosyası {{max}} MB'dan büyük olamaz.":"{{name}} cannot be larger than {{max}} MB.", "Ön yüz":"Front", "Arka yüz":"Back", "Dosya 1":"File 1", "Dosya 2":"File 2", "Kaldır":"Remove", "{{current}} / {{total}} yükleniyor":"Uploading {{current}} / {{total}}", "Yalnızca siz varsayılan olarak seçilisiniz. Belgeyi paylaşmak istediğiniz aile üyelerini ekleyebilirsiniz.":"Only you are selected by default. Add family members you want to share the document with.", "Seçili: {{count}} kişi":"Selected: {{count}} people",
  "Süresi yaklaşanları göster":"Show documents expiring soon", "Açıklama":"Description", "Görebilir":"Can view", "Versiyon Geçmişi":"Version History", "Görüntüle":"View", "İndir":"Download", "Düzenle":"Edit", "Güncelle":"Update", "Yeni dosyayı seç":"Select new file", "İptal":"Cancel", "Geçerlilik":"Validity", "Tüm ailenin görebildiği belgeler":"Documents visible to the whole family", "Tüm ailenin erişebildiği belgeler":"Documents accessible to the whole family", "Aile Üyeleri":"Family Members", "Çocuk / Bebek / Evcil Profilleri":"Child / Baby / Pet Profiles", "Süresi Yakın":"Expiring Soon", "Süresi Dolmuş":"Expired", "Kaydet":"Save",
  "Dosya {{max}} MB'dan büyük olamaz.":"File cannot be larger than {{max}} MB.",
  "Belge deposu sınırın {{max}} MB. Bu dosya ile toplam kullanım {{total}} MB olacağı için yükleme engellendi.":"Your document storage limit is {{max}} MB. Upload was blocked because total usage would be {{total}} MB with this file.",
  "Belgeler":"Documents", "Sınırsız belge yükleme":"Unlimited document uploads", "5 GB depolama alanı":"5 GB storage", "Kişisel ve ortak belgeler":"Personal and shared documents", "Geçerlilik tarihi takibi":"Validity date tracking",
  "Bu belgeyi silme yetkin yok.":"You do not have permission to delete this document.",
  "silinsin mi?":"should be deleted?",
  "Belge ara...":"Search documents...",
  "Sil":"Delete",
  "Yeni Versiyon":"New Version",
  "Mevcut":"Current",
  "Ad":"Name",
  "Ortak Belgeler":"Shared Documents",
  "Ortak":"Shared",
  "Toplam":"Total",
  "Ailenin Ortak Belgeleri":"Family Shared Documents",
  "belge":"document",
  "belge_plural":"documents",
  "Ben":"Me",
  "Manuel Profil":"Manual Profile",
  "Profil":"Profile",
  "Anne":"Mother",
  "Baba":"Father",
  "Çocuk":"Child",
  "Dede":"Grandfather",
  "Nene":"Grandmother",
  "Amca":"Uncle",
  "Teyze":"Aunt",
  "Dayı":"Uncle",
  "Bebek":"Baby",
  "Evcil Hayvan":"Pet",
  "Çocuk/Bebek":"Child/Baby",
  "Paylaşılan belge":"Shared document",
  "Kişisel belge":"Personal document",

  // Çocuk & Bebek Takibi / Health kayıtlarından gelen dinamik kategori başlıkları
  "Veteriner kontrolü":"Vet check",
  "İç parazit":"Internal parasite",
  "Dış parazit":"External parasite",
  "Aşı":"Vaccine",
  "İlaç":"Medicine",
  "Hastalık":"Illness",
  "Operasyon":"Operation",
  "Diş bakımı":"Dental care",
  "Çip":"Chip",
  "Tüy bakımı":"Grooming",
  "Banyo":"Bath",
  "Tırnak kesimi":"Nail trimming",
  "Kum temizliği":"Litter cleaning",
  "Kulak temizliği":"Ear cleaning",
  "Mama değişimi":"Food change",
  "Yürüyüş":"Walk",
  "Oyun":"Play",
  "Egzersiz":"Exercise",
  "Eğitim":"Training",
  "Davranış":"Behavior",
  "Sosyalleşme":"Socialization",
  "Tuvalet takibi":"Toilet tracking",
  "Ateş":"Fever",
  "Doktor kontrolü":"Doctor check",
  "Alerji":"Allergy",
  "Diş":"Tooth",
  "Rapor":"Report",
  "Acil durum":"Emergency",
  "İlk adım":"First step",
  "İlk kelime":"First word",
  "Doğum günü":"Birthday",
  "Okul":"School",
  "Gezi":"Trip",
  "Başarı":"Achievement",
  "Komik an":"Funny moment",
  "Özel an":"Special moment",
  "Fotoğraf":"Photo",
  "İlk gülümseme":"First smile",
  "Başını tuttu":"Held head up",
  "Döndü":"Rolled over",
  "Oturdu":"Sat up",
  "Emekledi":"Crawled",
  "Diş çıkardı":"Teething",
  "Sosyal gelişim":"Social development",
  "Davranış notu":"Behavior note"
};
const dmt = (lang, text, vars) => {
  let val = lang === "en" ? (DOC_LABELS[text] || text) : text;
  if (vars) Object.keys(vars).forEach(k => { val = val.replace(`{{${k}}}`, String(vars[k])); });
  return val;
};

const belgeSayisiText = (lang, count) => `${count} ${lang === "en" ? (count === 1 ? dmt(lang, "belge") : dmt(lang, "belge_plural")) : dmt(lang, "belge")}`;
const kategoriLabel = (lang, kategori) => kategori ? dmt(lang, kategori) : "";
const kategoriKisaLabel = (lang, kategori) => kategoriLabel(lang, kategori).replace(/^[^\s]+\s/, "");
const rolLabel = (lang, rol) => rol ? dmt(lang, rol) : "";

const ROL_IKONLARI = {
  "Anne": "👩", "Baba": "👨", "Çocuk": "👧", "Dede": "👴",
  "Nene": "👵", "Amca": "👨‍💼", "Teyze": "👩‍💼", "Dayı": "👨‍🦱",
  "Bebek": "🍼", "Evcil Hayvan": "🐾", "Çocuk/Bebek": "👶",
};


const DOCUMENT_PROFILE_AVATARS = {
  baby: ["18bebek.png", "19bebekkiz.png"],
  child: ["14cocuk2.png", "15cocuk4.png", "16cocuk1.png", "17cocuk3.png"],
  pet: ["20pet.png"],
};

const docProfileTypeFromData = (data = {}) => {
  const savedType = String(data.sahipProfileType || data.profileType || "").toLowerCase();
  if (savedType.includes("pet") || savedType.includes("evcil")) return "pet";
  if (savedType.includes("baby") || savedType.includes("bebek")) return "baby";
  if (savedType.includes("child") || savedType.includes("çocuk") || savedType.includes("cocuk")) return "child";

  const category = String(data.kategori || "").toLowerCase();
  if (category.includes("pet") || category.includes("evcil")) return "pet";
  if (category.includes("baby") || category.includes("bebek")) return "baby";

  const role = String(data.rol || "").toLowerCase();
  if (role.includes("pet") || role.includes("evcil")) return "pet";
  if (role === "bebek" || role === "baby") return "baby";
  return "child";
};

const stableDocAvatarSeed = (value = "") => {
  const str = String(value || "");
  let total = 0;
  for (let i = 0; i < str.length; i += 1) total += str.charCodeAt(i);
  return total;
};

const getDocumentProfileAvatar = (profile = {}) => {
  if (profile.avatar) return profile.avatar;
  if (profile.sahipAvatar) return profile.sahipAvatar;
  const type = docProfileTypeFromData(profile);
  const list = DOCUMENT_PROFILE_AVATARS[type] || DOCUMENT_PROFILE_AVATARS.child;
  return list[stableDocAvatarSeed(profile.uid || profile.sahipUid || profile.ad || profile.sahipAd) % list.length];
};

const MAX_MB = 10;

function boyutFormatla(b) {
  if (!b) return "";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
  return (b / (1024 * 1024)).toFixed(1) + " MB";
}

function sureDurumu(tarih, t) {
  if (!tarih) return null;
  const gun = Math.ceil((new Date(tarih) - new Date()) / 86400000);
  const daysLeft = t ? `${gun} ${t("documents.daysLeft") || "days left"}` : `${gun} days left`;
  if (gun < 0)   return { renk: "#ef4444", yazi: t ? t("documents.expired") : "Expired ⚠️", gun };
  if (gun <= 7)  return { renk: "#ef4444", yazi: daysLeft, gun };
  if (gun <= 30) return { renk: "#f97316", yazi: daysLeft, gun };
  if (gun <= 90) return { renk: "#eab308", yazi: daysLeft, gun };
  return { renk: "#22c55e", yazi: t ? t("documents.valid") : "Valid ✓", gun };
}

// mime type ("image/jpeg") veya uzantı ("jpeg") her ikisini de normalize et
function normalizeDosyaTipi(tip) {
  const t = (tip || "").toLowerCase().trim();
  // mime type → uzantı
  const mimeMap = {
    "image/jpeg": "jpg", "image/jpg": "jpg", "image/png": "png",
    "image/gif": "gif", "image/webp": "webp",
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  };
  return mimeMap[t] || t.split("/").pop() || t;
}

function dosyaIkonu(tip) {
  const tip2 = normalizeDosyaTipi(tip);
  if (["jpg","jpeg","png","gif","webp"].includes(tip2)) return "🖼️";
  if (tip2 === "pdf") return "📄";
  if (["doc","docx"].includes(tip2)) return "📝";
  if (["xls","xlsx"].includes(tip2)) return "📊";
  return "📎";
}

// AYLAR: component içinde tList("common.months") kullanılır
const AYLAR_DOCS_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
function tarihOkunakli(val, aylar = AYLAR_DOCS_TR) {
  if (!val) return "";
  try {
    const d = val?.toDate ? val.toDate() : new Date(val);
    if (isNaN(d)) return "";
    return `${d.getDate()} ${aylar[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return ""; }
}

/* ─────────────────────────────────────────
   YÜKLEME FORMU BİLEŞENİ
───────────────────────────────────────── */
function SeciliDosyaOnizleme({ dosya, etiket, onKaldir, kaldirmaEtiketi, kaldirmaDevreDisi = false }) {
  const [onizlemeUrl, setOnizlemeUrl] = useState("");
  const resimMi = String(dosya?.type || "").startsWith("image/");

  useEffect(() => {
    if (!dosya || !resimMi) {
      setOnizlemeUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(dosya);
    setOnizlemeUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [dosya, resimMi]);

  return (
    <div className="belgeDosyaOnizlemeKarti">
      <div className="belgeDosyaOnizlemeGorsel">
        {onizlemeUrl ? (
          <img src={onizlemeUrl} alt={dosya.name} />
        ) : (
          <span className="belgeDosyaOnizlemeIkon">{dosyaIkonu(dosya.name.split(".").pop())}</span>
        )}
        <span className="belgeDosyaOnizlemeEtiket">{etiket}</span>
        <button
          type="button"
          className="belgeDosyaKaldirBtn"
          onClick={onKaldir}
          aria-label={kaldirmaEtiketi}
          title={kaldirmaEtiketi}
          disabled={kaldirmaDevreDisi}
        >
          ×
        </button>
      </div>
      <div className="belgeDosyaOnizlemeBilgi">
        <span className="dosyaSeciliAd" title={dosya.name}>{dosya.name}</span>
        <span className="dosyaSeciliBoyut">{boyutFormatla(dosya.size)}</span>
      </div>
    </div>
  );
}

function belgeDosyalariniGetir(belge) {
  if (!belge) return [];
  const kayitliDosyalar = Array.isArray(belge.dosyalar) ? belge.dosyalar.filter(Boolean) : [];
  if (kayitliDosyalar.length) {
    return kayitliDosyalar.slice(0, 2).map((item, index) => ({
      ...item,
      sira: Number.isFinite(Number(item.sira)) ? Number(item.sira) : index,
      dosyaUrl: item.dosyaUrl || item.fileUrl || item.url || "",
      previewUrl: item.previewUrl || "",
      dosyaAdi: item.dosyaAdi || item.fileName || belge.dosyaAdi || belge.ad,
      dosyaBoyutu: Number(item.dosyaBoyutu ?? item.sizeBytes ?? 0),
      dosyaTipi: item.dosyaTipi || item.mimeType || belge.dosyaTipi,
    })).sort((a, b) => a.sira - b.sira);
  }
  if (!belge.dosyaUrl) return [];
  return [{
    sira: 0,
    dosyaUrl: belge.dosyaUrl,
    previewUrl: belge.previewUrl || "",
    fileKey: belge.fileKey || belge.dosyaPublicId || null,
    dosyaAdi: belge.dosyaAdi || belge.ad,
    dosyaBoyutu: Number(belge.dosyaBoyutu || 0),
    dosyaTipi: belge.dosyaTipi,
  }];
}

function BelgeOnizlemeSlider({ dosyalar, belgeAdi, aktifIndex, onAktifIndexDegis, onOpen, lang }) {
  const kaydirmaRef = useRef(null);
  const [hatalar, setHatalar] = useState({});
  const dosyaImzasi = dosyalar.map(item => `${item.fileKey || ""}:${item.dosyaUrl || ""}`).join("|");

  useEffect(() => {
    setHatalar({});
  }, [dosyaImzasi]);

  const noktayaGit = (index) => {
    const alan = kaydirmaRef.current;
    if (alan) alan.scrollTo({ left: alan.clientWidth * index, behavior: "smooth" });
    onAktifIndexDegis(index);
  };

  if (!dosyalar.length) return null;

  return (
    <div className="belgeOnizlemeSliderWrap noSwipeBack" data-no-swipe-back="true">
      <div
        ref={kaydirmaRef}
        className="belgeOnizlemeSlider"
        onScroll={(event) => {
          const alan = event.currentTarget;
          if (!alan.clientWidth) return;
          const yeniIndex = Math.max(0, Math.min(dosyalar.length - 1, Math.round(alan.scrollLeft / alan.clientWidth)));
          if (yeniIndex !== aktifIndex) onAktifIndexDegis(yeniIndex);
        }}
      >
        {dosyalar.map((item, index) => {
          const tip = normalizeDosyaTipi(item.dosyaTipi || item.dosyaAdi?.split(".").pop());
          const resimMi = ["jpg","jpeg","png","gif","webp"].includes(tip);
          const hataAnahtari = item.fileKey || item.dosyaUrl || String(index);
          return (
            <button type="button" className="belgeOnizlemeSlide belgeOnizlemeSlideButton" key={hataAnahtari} aria-label={`${index + 1} / ${dosyalar.length}`} onClick={() => onOpen?.(item)}>
              {resimMi && !hatalar[hataAnahtari] ? (
                <RenewableFileImage
                  src={getFileUrl(item.previewUrl || item.dosyaUrl)}
                  alt={`${belgeAdi} ${index + 1}`}
                  className="belgeCarouselImage"
                  decoding="async"
                  onError={() => setHatalar(prev => ({ ...prev, [hataAnahtari]: true }))}
                />
              ) : (
                <div className="belgeDosyaKart belgeCarouselDosyaKart">
                  <span className="belgeCarouselDosyaIkon">{resimMi ? "🖼️" : dosyaIkonu(tip)}</span>
                  <span className="belgeDosyaAd">{resimMi ? (lang === "en" ? "Preview could not be loaded." : "Önizleme yüklenemedi.") : item.dosyaAdi}</span>
                  <span className="belgeDosyaBoyut">{resimMi ? item.dosyaAdi : boyutFormatla(item.dosyaBoyutu)}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
      {dosyalar.length > 1 && (
        <div className="belgeOnizlemeNoktalar" role="tablist" aria-label={lang === "en" ? "Document pages" : "Belge sayfaları"}>
          {dosyalar.map((item, index) => (
            <button
              key={item.fileKey || item.dosyaUrl || index}
              type="button"
              className={`belgeOnizlemeNokta ${aktifIndex === index ? "aktif" : ""}`}
              onClick={() => noktayaGit(index)}
              aria-label={lang === "en" ? `Show page ${index + 1}` : `${index + 1}. sayfayı göster`}
              aria-selected={aktifIndex === index}
              role="tab"
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BelgeYukleForm({
  tip, sahipUid, sahipAd, uyeler, user,
  belgeYukle, yukleniyor, yuklemePct,
  onTamamlandi,
}) {
  const { t, lang } = useLanguage();
  const documentValidation = useFormValidation(`document-upload-${tip}`);
  const [ad, setAd]               = useState("");
  const [aciklama, setAciklama]   = useState("");
  const [kategori, setKategori]   = useState(KATEGORILER[1]);
  const [gecerlilik, setGecerlilik] = useState("");
  const [dosyalar, setDosyalar]   = useState([]);
  const [gorebilecekler, setGorebilecekler] = useState(() =>
    tip === "kisisel" && user?.uid ? [user.uid] : []
  );
  const [hata, setHata] = useState("");
  const [formYukleniyor, setFormYukleniyor] = useState(false);
  const [toplamYuklenecek, setToplamYuklenecek] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (tip !== "kisisel" || !user?.uid) return;
    setGorebilecekler(prev => prev.length ? prev : [user.uid]);
  }, [tip, user?.uid]);

  const kimlikBelgesiMi = kategori === KATEGORILER[1];
  const dosyaEtiketi = (index) => {
    if (kimlikBelgesiMi) return dmt(lang, index === 0 ? "Ön yüz" : "Arka yüz");
    return dmt(lang, index === 0 ? "Dosya 1" : "Dosya 2");
  };

  const toggleGorebilir = (uid) => {
    setGorebilecekler(prev =>
      prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]
    );
  };

  const sifirla = () => {
    setAd(""); setAciklama(""); setKategori(KATEGORILER[1]);
    setGecerlilik(""); setDosyalar([]); setHata("");
    setGorebilecekler(tip === "kisisel" && user?.uid ? [user.uid] : []);
    setToplamYuklenecek(0);
    documentValidation.resetErrors();
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDosyaSec = (event) => {
    const secilenler = Array.from(event.target.files || []);
    if (!secilenler.length) return;
    documentValidation.clearError("files");

    setDosyalar(prev => {
      const benzersiz = [...prev, ...secilenler].filter((dosya, index, liste) =>
        liste.findIndex(item =>
          item.name === dosya.name && item.size === dosya.size && item.lastModified === dosya.lastModified
        ) === index
      );
      if (benzersiz.length > 2) setHata(dmt(lang, "En fazla 2 dosya seçebilirsiniz."));
      else setHata("");
      return benzersiz.slice(0, 2);
    });
    event.target.value = "";
  };

  const dosyaKaldir = (index) => {
    if (formYukleniyor) return;
    setDosyalar(prev => prev.filter((_, i) => i !== index));
    setHata("");
  };

  const handleYukle = async () => {
    if (!documentValidation.validate([
      !ad.trim() && "name",
      !dosyalar.length && "files",
    ])) return;
    const buyukDosya = dosyalar.find(dosya => dosya.size > MAX_MB * 1024 * 1024);
    if (buyukDosya) {
      setHata(dmt(lang, "{{name}} dosyası {{max}} MB'dan büyük olamaz.", { name: buyukDosya.name, max: MAX_MB }));
      return;
    }

    const yuklenecekler = [...dosyalar];
    const toplamDosyaBoyutu = yuklenecekler.reduce((toplam, dosya) => toplam + Number(dosya.size || 0), 0);
    setHata("");
    setFormYukleniyor(true);
    setToplamYuklenecek(yuklenecekler.length);
    try {
      const sonuc = await belgeYukle({
        dosya: yuklenecekler[0],
        dosyalar: yuklenecekler,
        ad: ad.trim(),
        aciklama, kategori,
        gecerlilikTarihi: gecerlilik || null,
        tip,
        sahipUid,
        sahipAd,
        gorebilecekler: tip === "ortak" ? [] : gorebilecekler,
        toplamDosyaSayisi: yuklenecekler.length,
        toplamDosyaBoyutu,
      });
      if (sonuc === false) return false;
      sifirla();
      onTamamlandi?.();
      return true;
    } catch (e) {
      setHata(`${t("documents.uploadFailed")}: ${e.message}`);
      throw e;
    } finally {
      setFormYukleniyor(false);
      setToplamYuklenecek(0);
    }
  };

  return (
    <div className="darkCard belgeYukleKarti">
      <div className="cardHeader belgeYukleHeader">
        <span className="cardHeaderTitle">📎 {dmt(lang, "Belge Yükle")}</span>
        <span className="belgeDosyaLimitRozeti">{dmt(lang, "En fazla 2 dosya")}</span>
      </div>
      <div className="formBody">

        {dosyalar.length > 0 && (
          <div className="belgeSecilenDosyalar">
            <div className="belgeSecilenDosyalarBaslik">
              <span>{dmt(lang, "Seçilen dosyalar")}</span>
              <span>{dmt(lang, "{{count}} dosya seçildi", { count: dosyalar.length })}</span>
            </div>
            <div className={`belgeDosyaOnizlemeGrid ${dosyalar.length === 1 ? "tek" : ""}`}>
              {dosyalar.map((dosya, index) => (
                <SeciliDosyaOnizleme
                  key={`${dosya.name}-${dosya.size}-${dosya.lastModified}`}
                  dosya={dosya}
                  etiket={dosyaEtiketi(index)}
                  kaldirmaEtiketi={dmt(lang, "Kaldır")}
                  kaldirmaDevreDisi={formYukleniyor}
                  onKaldir={() => dosyaKaldir(index)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Bir veya iki dosya seçici */}
        {dosyalar.length < 2 && (
          <button
            type="button"
            className={documentValidation.fieldClass("files", `dosyaSecArea ${dosyalar.length === 1 ? "ikinciDosya" : ""}`)}
            data-required-field={`document-upload-${tip}-files`}
            onClick={() => inputRef.current?.click()}
            disabled={formYukleniyor}
          >
            <div className="dosyaSecPlaceholder">
              <span className="dosyaSecIkon">{dosyalar.length === 1 ? "＋" : "📂"}</span>
              <span>{dmt(lang, dosyalar.length === 1 ? "İkinci dosyayı ekle" : "1 veya 2 dosya seçin")}</span>
              <span className="dosyaSecAlt">PDF, JPG, PNG, DOCX · {dmt(lang, "Dosya başına maksimum {{max}} MB", { max: MAX_MB })}</span>
            </div>
          </button>
        )}
        <input ref={inputRef} type="file"
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx"
          multiple
          style={{display:"none"}}
          onChange={handleDosyaSec}
        />

        <input className={documentValidation.fieldClass("name", "darkInput")} data-required-field={`document-upload-${tip}-name`} aria-invalid={documentValidation.hasError("name")} placeholder={dmt(lang, "Belge adı *")} value={ad} onChange={e=>{ setAd(e.target.value); documentValidation.clearError("name"); }}/>
        <textarea className="darkInput" placeholder={dmt(lang, "Açıklama (isteğe bağlı)")} value={aciklama}
          onChange={e=>setAciklama(e.target.value)} rows={2}/>

        <div className="formRow">
          <div className="inputLabelGroup">
            <label className="darkLabel">{dmt(lang, "Kategori")}</label>
            <select className="darkInput" value={kategori} onChange={e=>setKategori(e.target.value)}>
              {KATEGORILER.filter(k=>k!=="Tümü").map(k=><option key={k} value={k}>{dmt(lang, k)}</option>)}
            </select>
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">{dmt(lang, "Geçerlilik Tarihi")}</label>
            <input className="darkInput" type="date" value={gecerlilik} onChange={e=>setGecerlilik(e.target.value)}/>
          </div>
        </div>

        {/* Kişisel belgelerde kimler görebilir */}
        {tip === "kisisel" && (
          <div className="belgeGorebilirWrap">
            <div className="belgeGorebilirBaslikSatiri">
              <label className="darkLabel">👁️ {dmt(lang, "Kimler görebilir?")}</label>
              <span className="belgeGorebilirSayac">{dmt(lang, "Seçili: {{count}} kişi", { count: gorebilecekler.length })}</span>
            </div>
            <p className="belgeGorebilirAciklama">{dmt(lang, "Yalnızca siz varsayılan olarak seçilisiniz. Belgeyi paylaşmak istediğiniz aile üyelerini ekleyebilirsiniz.")}</p>
            <div className="belgeGorebilirGrid">
              {uyelerSirala(uyeler).map(u => {
                const secili = gorebilecekler.includes(u.uid);
                const benMi = u.uid === user?.uid;
                return (
                  <button key={u.uid} type="button"
                    className={`belgeGorebilirBtn ${secili ? "secili" : ""}`}
                    onClick={() => toggleGorebilir(u.uid)}
                    style={secili ? {borderColor: u.calendarColor||"#2563eb", background: (u.calendarColor||"#2563eb")+"22"} : {}}>
                    <span className="belgeGorebilirAvatar">
                      <AvatarImg
                        avatar={u.manualDocumentProfile ? getDocumentProfileAvatar(u) : (u.avatar || null)}
                        renk={u.calendarColor || "#2563eb"}
                        initials={u.manualDocumentProfile ? (ROL_IKONLARI[u.rol] || `${u.ad?.[0] || ""}`) : `${u.ad?.[0] || ""}${u.soyad?.[0] || ""}`}
                        size={30}
                        glow={false}
                        radius="50%"
                        uid={u.uid || u.id}
                        isOnline={u.isOnline === true || u.online === true}
                      />
                    </span>
                    <span className="belgeGorebilirAd">{u.ad}</span>
                    {benMi && <span className="belgeGorebilirBenRozeti">{dmt(lang, "Ben")}</span>}
                    {secili && <span className="belgeGorebilirCheck">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <FormValidationNotice
          errors={documentValidation.errors}
          labels={{ name: dmt(lang, "Belge adı"), files: dmt(lang, "Dosya") }}
        />
        {hata && <div className="profilError">{hata}</div>}

        {(formYukleniyor || yukleniyor) && (
          <div className="yuklemeCubugu">
            <div className="yuklemeCubuguIc" style={{width:`${yuklemePct}%`}}/>
            <span className="yuklemeCubuguYazi">
              {toplamYuklenecek > 1 && `${dmt(lang, "{{current}} / {{total}} yükleniyor", {
                current: Math.min(toplamYuklenecek, Math.floor((Math.min(99, Number(yuklemePct) || 0) * toplamYuklenecek) / 100) + 1),
                total: toplamYuklenecek,
              })} · `}
              %{yuklemePct}
            </span>
          </div>
        )}
        <div className="formRow">
          <AsyncActionButton
            type="button"
            className="primaryButton"
            onAction={handleYukle}
            externalBusy={formYukleniyor || yukleniyor}
            busyText={dmt(lang, "yükleniyor...")}
          >
            ⬆️ {dmt(lang, "Yükle")}
          </AsyncActionButton>
          <button type="button" className="darkSecondaryBtn" style={{flex:1,marginTop:0}} onClick={sifirla} disabled={formYukleniyor || yukleniyor}>{dmt(lang, "Temizle")}</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   BELGE LİSTESİ BİLEŞENİ
───────────────────────────────────────── */
function BelgeListesi({
  belgeler, user, uyeler, yoneticiMi,
  belgeGuncelle, belgeSil, belgeVersiyonEkle,
  yukleniyor, yuklemePct,
  className = "",
}) {
  const { t, tList, lang } = useLanguage();
  const AYLAR = tList("common.months");
  const [aramaMetni, setAramaMetni]         = useState("");
  const [seciliKategori, setSeciliKategori] = useState("Tümü"); // will sync with lang
  const [sureFiltreAcik, setSureFiltreAcik] = useState(false);
  const [seciliBelge, setSeciliBelge]       = useState(null);
  const [indiriliyor,  setIndiriliyor]      = useState(false);
  const [aktifOnizlemeIndex, setAktifOnizlemeIndex] = useState(0);
  const [secureViewerItem, setSecureViewerItem] = useState(null);

  const fetchBelgeBlob = async (url) => {
    const fileUrl = getFileFetchUrl(url);
    if (!fileUrl) throw new Error("Dosya URL bulunamadı.");

    const res = await fetchProtectedFile(fileUrl, {
      method: "GET",
      cache: "no-store",
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
  };

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const safeDosyaAdi = (dosyaAdi, mimeType, fallback = "belge") => {
    const clean = String(dosyaAdi || fallback).replace(/[\/:*?"<>|]+/g, "_");
    if (clean.includes(".")) return clean;
    const ext = normalizeDosyaTipi(mimeType);
    return ext ? `${clean}.${ext === "jpg" ? "jpg" : ext}` : clean;
  };

  useEffect(() => {
    setAktifOnizlemeIndex(0);
  }, [seciliBelge?.id]);

  // Dosyayı auth ile çek; iOS'ta paylaşım menüsünü aç, Safari'ye yönlendirme yapma.
  const handleIndir = async (url, dosyaAdi, mimeType) => {
    if (indiriliyor) return;

    // ANDROID (MADDE 8): iOS'taki gibi CİHAZIN KENDİ paylaş/kaydet sayfası açılır.
    //
    // Önceki sürümde dosya URL'si sistem tarayıcısında açılıyordu; bu, "Görüntüle"
    // ile aynı sonucu veriyor ve dosyayı sadece gösteriyordu — kullanıcı indirme
    // akışı yaşamıyordu.
    //
    // Doğru yöntem: dosyayı kimlik doğrulamalı olarak indirip (blob), Android'in
    // yerleşik PAYLAŞ/KAYDET sayfasını Web Share API Level 2 ile açmak. Bu sayfa
    // "Dosyalara kaydet", "Drive'a yükle", "WhatsApp ile gönder" gibi seçenekleri
    // sunar; iOS'taki paylaşım sayfasının Android karşılığıdır.
    //
    // Capacitor Android WebView, https://localhost üzerinden çalıştığı için güvenli
    // bağlam sayılır ve navigator.share(files) desteklenir. Aşağıdaki ortak akış
    // zaten bu yolu içerdiğinden Android için AYRI bir dal tutmuyoruz; yalnızca
    // paylaşım hiç desteklenmiyorsa (çok eski WebView) tarayıcıya düşülür.
    // Ekstra Capacitor plugin'i gerekmez; Google Play açısından ek izin doğurmaz.

    setIndiriliyor(true);
    try {
      const blob = await fetchBelgeBlob(url);
      const finalMime = blob.type || (String(mimeType || "").includes("/") ? mimeType : "application/octet-stream");
      const finalName = safeDosyaAdi(dosyaAdi, mimeType || finalMime);

      const nativeBridge = window?.webkit?.messageHandlers?.aileNativeBridge;
      if (nativeBridge) {
        const base64 = await blobToBase64(blob);
        nativeBridge.postMessage({
          action: "shareFile",
          base64,
          mimeType: finalMime,
          fileName: finalName,
        });
        return;
      }

      if (navigator?.canShare && window.File) {
        const file = new File([blob], finalName, { type: finalMime });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: finalName });
          return;
        }
      }

      // ANDROID (MADDE 8): Dosyayı önbelleğe yazıp Android'in yerleşik
      // PAYLAŞ/KAYDET sayfasını aç ("Dosyalara kaydet", "Drive'a yükle" vb.).
      // Depolama izni gerektirmez; Play uyumludur.
      if (isAndroidPlatform()) {
        const paylasildi = await androidDosyaPaylas(blob, finalName, finalName);
        if (paylasildi) return;

        // Paylaşım hiç kullanılamıyorsa son çare: tarayıcıda aç.
        const fileUrl = getFileFetchUrl(url);
        let acildi = false;
        try {
          if (fileUrl && CapacitorApp && CapacitorApp.openUrl) {
            await CapacitorApp.openUrl({ url: fileUrl });
            acildi = true;
          }
        } catch (_) { /* çözülemedi -> pencere yedeği */ }
        if (!acildi && fileUrl) acildi = Boolean(window.open(fileUrl, "_blank", "noopener"));
        if (!acildi) throw new Error("Harici açılış başarısız.");
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (_) {
      await appAlert(lang === "en" ? "File could not be downloaded." : "Dosya indirilemedi.");
    } finally {
      setIndiriliyor(false);
    }
  };
  const [duzenleModal, setDuzenleModal]     = useState(null);
  const [versiyonModal, setVersiyonModal]   = useState(null);
  const vDosyaInputRef = useRef(null);

  const filtreli = belgeler.filter(b => {
    if (seciliKategori !== "Tümü" && b.kategori !== seciliKategori) return false;
    if (aramaMetni && !b.ad?.toLowerCase().includes(aramaMetni.toLowerCase())) return false;
    if (sureFiltreAcik) {
      const s = sureDurumu(b.gecerlilikTarihi, t);
      if (!s || s.gun > 90) return false;
    }
    return true;
  });

  const handleSil = async (belge) => {
    if (!yoneticiMi && belge.yukleyenUid !== user?.uid) { appAlert(dmt(lang, "Bu belgeyi silme yetkin yok.")); return; }
    if (!await appConfirm(lang === "en" ? `"${belge.ad}" ${dmt(lang, "silinsin mi?")}` : `"${belge.ad}" silinsin mi?`)) return;
    await belgeSil({ belgeId: belge.id });
    setSeciliBelge(null);
  };

  const handleDuzenleKaydet = async () => {
    if (!duzenleModal) return;
    const sahipUye = uyeler.find(u => u.uid === duzenleModal.sahipUid);
    await belgeGuncelle({ belgeId: duzenleModal.id, guncel: {
      ad: duzenleModal.ad, aciklama: duzenleModal.aciklama,
      kategori: duzenleModal.kategori,
      gecerlilikTarihi: duzenleModal.gecerlilikTarihi,
      gorebilecekler: duzenleModal.gorebilecekler,
      sahipUid: duzenleModal.sahipUid,
      sahipAd: sahipUye ? `${sahipUye.ad} ${sahipUye.soyad}` : duzenleModal.sahipAd,
    }});
    setDuzenleModal(null); setSeciliBelge(null);
  };

  const handleVersiyonYukle = async (dosya) => {
    if (!versiyonModal) return;
    if (dosya.size > MAX_MB * 1024 * 1024) { appAlert(dmt(lang, "Dosya {{max}} MB'dan büyük olamaz.", { max: MAX_MB })); return; }
    await belgeVersiyonEkle({ belgeId: versiyonModal.id, eskiVeri: versiyonModal, dosya });
    setVersiyonModal(null); setSeciliBelge(null);
  };

  return (
    <>
      <div className={`belgeListesiPanel ${className}`.trim()}>
      {/* Arama */}
      <div className="belgeAramaWrap">
        <span className="belgeAramaIkon">🔍</span>
        <input className="belgeAramaInput" placeholder={dmt(lang, "Belge ara...")} value={aramaMetni} onChange={e=>setAramaMetni(e.target.value)}/>
        {aramaMetni && <button className="belgeAramaSil" onClick={()=>setAramaMetni("")}>×</button>}
      </div>

      {/* Kategori chips */}
      <div className="categoryChips noSwipeBack" data-no-swipe-back="true" style={{marginBottom:"8px"}}> 
        {KATEGORILER.map(k=>(
          <button key={k} className={seciliKategori===k?"chip activeBlue":"chip"} onClick={()=>setSeciliKategori(k)}>{dmt(lang, k)}</button>
        ))}
      </div>

      {/* Süre filtresi */}
      <div className="darkCard" style={{padding:"12px 16px",marginBottom:"10px"}}>
        <label className="toggleLine">
          <span className="toggleLabelText">⚠️ {dmt(lang, "Süresi yaklaşanları göster")}</span>
          <div className={`toggleSwitch ${sureFiltreAcik?"on":""}`} onClick={()=>setSureFiltreAcik(!sureFiltreAcik)}>
            <div className="toggleThumb"/>
          </div>
        </label>
      </div>

      {/* Liste */}
      <div className="tableCard">
        {filtreli.length === 0 ? (
          <div className="emptyState">{t("documents.empty")}</div>
        ) : (
          filtreli.map((b, idx) => {
            const sure = sureDurumu(b.gecerlilikTarihi, t);
            return (
              <div key={b.id} className={`tableRow ${idx===filtreli.length-1?"tableRowLast":""}`}
                onClick={()=>setSeciliBelge(b)} style={{cursor:"pointer"}}>
                <div className="belgeIkon">{dosyaIkonu(b.dosyaTipi)}</div>
                <div className="tableRowContent">
                  <div style={{display:"flex",alignItems:"center",gap:"6px",flexWrap:"wrap"}}>
                    <span className="tableRowTitle">{b.ad}</span>
                    {belgeDosyalariniGetir(b).length > 1 && <span className="belgeDosyaAdetBadge">▣ {belgeDosyalariniGetir(b).length}</span>}
                    {b.versiyonlar?.length > 0 && <span className="belgeVBadge">v{b.versiyonlar.length+1}</span>}
                  </div>
                  <span className="tableRowSub">
                    {kategoriKisaLabel(lang, b.kategori)} · {b.yukleyenAd} · {boyutFormatla(b.dosyaBoyutu)}
                  </span>
                  {sure && (
                    <span className="belgeSureBadge" style={{background:sure.renk+"22",color:sure.renk}}>
                      ⏰ {sure.yazi}
                    </span>
                  )}
                </div>
                <span className="tableRowChevron">›</span>
              </div>
            );
          })
        )}
      </div>

      </div>

      {/* ── DETAY MODAL ── */}
      {seciliBelge && (
        <div className="modalOverlay" onClick={()=>setSeciliBelge(null)}>
          <div className="darkModal belgeDetayModal" onClick={e=>e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">{dosyaIkonu(seciliBelge.dosyaTipi)} {seciliBelge.ad}</span>
              <button className="ayarKapatBtn" onClick={()=>setSeciliBelge(null)}>×</button>
            </div>

            <BelgeOnizlemeSlider
              key={seciliBelge.id}
              dosyalar={belgeDosyalariniGetir(seciliBelge)}
              belgeAdi={seciliBelge.ad}
              aktifIndex={aktifOnizlemeIndex}
              onAktifIndexDegis={setAktifOnizlemeIndex}
              onOpen={(aktifDosya) => setSecureViewerItem({
                url: aktifDosya.dosyaUrl,
                fileName: aktifDosya.dosyaAdi || seciliBelge.ad,
                mimeType: aktifDosya.dosyaTipi,
                title: seciliBelge.ad || aktifDosya.dosyaAdi,
              })}
              lang={lang}
            />

            <div className="belgeMetaList">
              {[
                [t("documents.lbl.category"),    kategoriLabel(lang, seciliBelge.kategori)],
                [t("documents.lbl.owner"),      seciliBelge.sahipAd === "Ortak" ? dmt(lang, "Ortak") : seciliBelge.sahipAd],
                [t("documents.lbl.uploader"),    seciliBelge.yukleyenAd],
                [t("documents.lbl.added"),    tarihOkunakli(seciliBelge.eklenmeTarihi, AYLAR)],
                seciliBelge.gecerlilikTarihi && [t("documents.lbl.expiry"), tarihOkunakli(seciliBelge.gecerlilikTarihi, AYLAR)],
              ].filter(Boolean).map(([lbl,val])=>(
                <div key={lbl} className="belgeMetaRow">
                  <span className="belgeMetaLbl">{lbl}</span>
                  <span className="belgeMetaVal">{val}</span>
                </div>
              ))}
              {seciliBelge.aciklama && (
                <div className="belgeMetaRow">
                  <span className="belgeMetaLbl">📝 {dmt(lang, "Açıklama")}</span>
                  <span className="belgeMetaVal">{seciliBelge.aciklama}</span>
                </div>
              )}
              {seciliBelge.tip === "kisisel" && seciliBelge.gorebilecekler?.length > 0 && (
                <div className="belgeMetaRow">
                  <span className="belgeMetaLbl">👁️ {dmt(lang, "Görebilir")}</span>
                  <span className="belgeMetaVal">
                    {uyeler.filter(u=>seciliBelge.gorebilecekler.includes(u.uid)).map(u=>u.ad).join(", ")}
                  </span>
                </div>
              )}
            </div>

            {/* Süre uyarısı */}
            {(() => { const s=sureDurumu(seciliBelge.gecerlilikTarihi, t); if(!s)return null;
              return <div className="belgeSureUyari" style={{borderColor:s.renk,background:s.renk+"18"}}><span style={{color:s.renk,fontWeight:800}}>⏰ {s.yazi}</span></div>;
            })()}

            {/* Versiyon geçmişi */}
            {seciliBelge.versiyonlar?.length > 0 && (
              <div className="belgeVersiyonlar">
                <div className="belgeVersiyonBaslik">📚 {dmt(lang, "Versiyon Geçmişi")}</div>
                {seciliBelge.versiyonlar.map((v,i)=>(
                  <div key={i} className="belgeVersiyonRow">
                    <span className="belgeVersiyonNo">v{i+1}</span>
                    <div style={{flex:1}}>
                      <span className="belgeVersiyonAd">{v.dosyaAdi}</span>
                      <span className="belgeVersiyonTarih">{tarihOkunakli(v.tarih, AYLAR)} · {v.yukleyenAd}</span>
                    </div>
                    <button type="button" className="belgeVersiyonIndir" onClick={() => setSecureViewerItem({
                      url: v.dosyaUrl,
                      fileName: v.dosyaAdi,
                      mimeType: v.dosyaTipi,
                      title: v.dosyaAdi,
                    })} style={{ border: 0, padding: 0, cursor: "pointer" }} aria-label={lang === "en" ? "View this version" : "Bu sürümü görüntüle"}><MediaActionIcon type="view" /></button>
                  </div>
                ))}
              </div>
            )}

            <div className="belgeAksiyonlar">
              <button type="button" className="belgeAksiyonBtn primary" onClick={() => {
                const aktifDosya = belgeDosyalariniGetir(seciliBelge)[aktifOnizlemeIndex] || seciliBelge;
                setSecureViewerItem({
                  url: aktifDosya.dosyaUrl,
                  fileName: aktifDosya.dosyaAdi || seciliBelge.ad,
                  mimeType: aktifDosya.dosyaTipi,
                  title: seciliBelge.ad || aktifDosya.dosyaAdi,
                });
              }}><MediaActionIcon type="view" /> {dmt(lang, "Görüntüle")}</button>
              <button
  className="belgeAksiyonBtn secondary"
  onClick={() => {
    const aktifDosya = belgeDosyalariniGetir(seciliBelge)[aktifOnizlemeIndex] || seciliBelge;
    handleIndir(aktifDosya.dosyaUrl, aktifDosya.dosyaAdi, aktifDosya.dosyaTipi);
  }}
  disabled={indiriliyor}
>
  {indiriliyor ? <MediaActionLoading /> : <MediaActionIcon type="download" />} {dmt(lang, "İndir")}
</button>
              {(yoneticiMi || seciliBelge.yukleyenUid===user?.uid) && (
                <button className="belgeAksiyonBtn edit" onClick={()=>setDuzenleModal({...seciliBelge})}><MediaActionIcon type="edit" /> {dmt(lang, "Düzenle")}</button>
              )}
              {(yoneticiMi || seciliBelge.yukleyenUid===user?.uid) && (
                <button className="belgeAksiyonBtn version" onClick={()=>setVersiyonModal(seciliBelge)}><MediaActionIcon type="update" /> {dmt(lang, "Güncelle")}</button>
              )}
              {(yoneticiMi || seciliBelge.yukleyenUid===user?.uid) && (
                <button className="belgeAksiyonBtn danger" onClick={()=>handleSil(seciliBelge)}><MediaActionIcon type="delete" /> {dmt(lang, "Sil")}</button>
              )}
            </div>
          </div>
        </div>
      )}
      <SecureMediaViewer open={!!secureViewerItem} item={secureViewerItem} onClose={() => setSecureViewerItem(null)} lang={lang} />

      {/* ── VERSİYON MODAL ── */}
      {versiyonModal && (
        <div className="modalOverlay" onClick={()=>setVersiyonModal(null)}>
          <div className="darkModal" onClick={e=>e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">🔄 {dmt(lang, "Yeni Versiyon")}</span>
              <button className="ayarKapatBtn" onClick={()=>setVersiyonModal(null)}>×</button>
            </div>
            <p className="darkModalSub">{dmt(lang, "Mevcut")}: <strong style={{color:"#e2e8f0"}}>{versiyonModal.ad}</strong></p>
            <div className="dosyaSecArea" onClick={()=>vDosyaInputRef.current?.click()}>
              <div className="dosyaSecPlaceholder">
                <span style={{fontSize:"32px"}}>📂</span>
                <span>{dmt(lang, "Yeni dosyayı seç")}</span>
                <span className="dosyaSecAlt">{dmt(lang, "Maks")} {MAX_MB} MB</span>
              </div>
            </div>
            <input ref={vDosyaInputRef} type="file" style={{display:"none"}}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx"
              onChange={e=>{if(e.target.files[0]) handleVersiyonYukle(e.target.files[0]);}}/>
            {yukleniyor && (
              <div className="yuklemeCubugu" style={{marginTop:"12px"}}>
                <div className="yuklemeCubuguIc" style={{width:`${yuklemePct}%`}}/>
                <span className="yuklemeCubuguYazi">%{yuklemePct}</span>
              </div>
            )}
            <button className="darkSecondaryBtn" style={{marginTop:"10px"}} onClick={()=>setVersiyonModal(null)}>{dmt(lang, "İptal")}</button>
          </div>
        </div>
      )}

      {/* ── DÜZENLEME MODAL ── */}
      {duzenleModal && (
        <div className="modalOverlay" onClick={()=>setDuzenleModal(null)}>
          <div className="darkModal" onClick={e=>e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">✏️ {dmt(lang, "Düzenle")}</span>
              <button className="ayarKapatBtn" onClick={()=>setDuzenleModal(null)}>×</button>
            </div>
            <div className="formBody" style={{marginTop:"10px"}}>
              <input className="darkInput" placeholder={dmt(lang, "Ad")} value={duzenleModal.ad}
                onChange={e=>setDuzenleModal(p=>({...p,ad:e.target.value}))}/>
              <textarea className="darkInput" placeholder={dmt(lang, "Açıklama")} value={duzenleModal.aciklama||""}
                onChange={e=>setDuzenleModal(p=>({...p,aciklama:e.target.value}))} rows={2}/>
              <div className="formRow">
                <div className="inputLabelGroup">
                  <label className="darkLabel">{dmt(lang, "Kategori")}</label>
                  <select className="darkInput" value={duzenleModal.kategori}
                    onChange={e=>setDuzenleModal(p=>({...p,kategori:e.target.value}))}>
                    {duzenleModal.kategori && !KATEGORILER.includes(duzenleModal.kategori) && (
                      <option value={duzenleModal.kategori}>{kategoriLabel(lang, duzenleModal.kategori)}</option>
                    )}
                    {KATEGORILER.filter(k=>k!=="Tümü").map(k=><option key={k} value={k}>{dmt(lang, k)}</option>)}
                  </select>
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{dmt(lang, "Geçerlilik")}</label>
                  <input className="darkInput" type="date" value={duzenleModal.gecerlilikTarihi||""}
                    onChange={e=>setDuzenleModal(p=>({...p,gecerlilikTarihi:e.target.value}))}/>
                </div>
              </div>
              {duzenleModal.tip === "kisisel" && (
                <div className="belgeGorebilirWrap">
                  <label className="darkLabel" style={{marginBottom:"8px",display:"block"}}>👁️ {dmt(lang, "Kimler görebilir?")}</label>
                  <div className="belgeGorebilirGrid">
                    {uyelerSirala(uyeler).map(u=>{
                      const secili = (duzenleModal.gorebilecekler||[]).includes(u.uid);
                      return (
                        <button key={u.uid}
                          className={`belgeGorebilirBtn ${secili?"secili":""}`}
                          style={secili ? {borderColor:u.calendarColor||"#2563eb",background:(u.calendarColor||"#2563eb")+"22"} : {}}
                          onClick={()=>setDuzenleModal(p=>({...p,gorebilecekler:secili?p.gorebilecekler.filter(x=>x!==u.uid):[...(p.gorebilecekler||[]),u.uid]}))}>
                          <span>{ROL_IKONLARI[u.rol]||"👤"}</span>
                          <span>{u.ad}</span>
                          {secili && <span className="belgeGorebilirCheck">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <button className="primaryButton" onClick={handleDuzenleKaydet}>{dmt(lang, "Kaydet")}</button>
              <button className="darkSecondaryBtn" style={{marginTop:"8px"}} onClick={()=>setDuzenleModal(null)}>{dmt(lang, "İptal")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────
   ORTAK BELGELER SAYFASI
───────────────────────────────────────── */
function OrtakBelgelerSayfasi({ belgeler, uyeler, user, yoneticiMi, canUpload, belgeYukle, belgeGuncelle, belgeSil, belgeVersiyonEkle, yukleniyor, yuklemePct, onGeri }) {
  const { t, lang } = useLanguage();
  const [yukleAcik, setYukleAcik] = useState(false);
  const ortakBelgeler = belgeler.filter(b => b.tip === "ortak");

  return (
    <div className="pageContent belgeSabitSayfa belgeAltSayfa">
      <div className="belgeAltSayfaHeader">
        <button className="belgeGeriBtn" onClick={onGeri}>‹</button>
        <div>
          <div className="belgeAltSayfaBaslik">📁 {dmt(lang, "Ortak Belgeler")}</div>
          <div className="belgeAltSayfaAlt">{dmt(lang, "Tüm ailenin görebildiği belgeler")}</div>
        </div>
        {canUpload && (
          <button className="belgeYukleHizliBtn" onClick={()=>setYukleAcik(!yukleAcik)}>
            {yukleAcik ? "−" : `+ ${t("documents.upload")}`}
          </button>
        )}
      </div>

      {yukleAcik && (
        <BelgeYukleForm
          tip="ortak"
          sahipUid={null}
          sahipAd="Ortak"
          uyeler={uyeler}
          user={user}
          belgeYukle={belgeYukle}
          yukleniyor={yukleniyor}
          yuklemePct={yuklemePct}
          onTamamlandi={()=>setYukleAcik(false)}
        />
      )}

      <BelgeListesi
        belgeler={ortakBelgeler}
        user={user} uyeler={uyeler} yoneticiMi={yoneticiMi}
        belgeGuncelle={belgeGuncelle} belgeSil={belgeSil}
        belgeVersiyonEkle={belgeVersiyonEkle}
        yukleniyor={yukleniyor} yuklemePct={yuklemePct}
        className="belgeDetayListePanel"
      />
    </div>
  );
}

/* ─────────────────────────────────────────
   KİŞİSEL BELGELER SAYFASI
───────────────────────────────────────── */
function KisiselBelgelerSayfasi({ uye, belgeler, uyeler, user, yoneticiMi, canUpload, belgeYukle, belgeGuncelle, belgeSil, belgeVersiyonEkle, yukleniyor, yuklemePct, onGeri }) {
  const { t, lang } = useLanguage();
  const [yukleAcik, setYukleAcik] = useState(false);

  // Bu üyeye ait VE kullanıcının görebileceği belgeler
  const kisiselBelgeler = belgeler.filter(b => {
    if (b.tip !== "kisisel") return false;
    if (b.sahipUid !== uye.uid) return false;
    // Yönetici her zaman görür
if (yoneticiMi) return true;
// Belge sahibi kendi belgesini her zaman görür
if (b.sahipUid === user?.uid) return true;
// Belgeyi yükleyen her zaman görür
if (b.yukleyenUid === user?.uid) return true;
    // gorebilecekler boşsa veya yoksa sadece yükleyen görür
    if (!b.gorebilecekler || b.gorebilecekler.length === 0) return false;
    // Kullanıcı izin listesinde var mı?
    return b.gorebilecekler.includes(user?.uid);
  });

  const kendiMi = uye.uid === user?.uid;
  const renk = uye.calendarColor || "#2563eb";

  return (
    <div className="pageContent belgeSabitSayfa belgeAltSayfa">
      <div className="belgeAltSayfaHeader">
        <button className="belgeGeriBtn" onClick={onGeri}>‹</button>
        <div className="memberAvatar" style={{flexShrink:0}}>
          <AvatarImg
            avatar={uye.manualDocumentProfile ? getDocumentProfileAvatar(uye) : (uye.avatar || null)}
            renk={renk}
            initials={uye.manualDocumentProfile ? (ROL_IKONLARI[uye.rol] || `${uye.ad?.[0]||""}`) : `${uye.ad?.[0]||""}${uye.soyad?.[0]||""}`}
            size={38}
            glow={false}
            radius="50%"
          />
        </div>
        <div>
          <div className="belgeAltSayfaBaslik">{uye.ad} {uye.soyad}</div>
          <div className="belgeAltSayfaAlt">{ROL_IKONLARI[uye.rol]||"👤"} {rolLabel(lang, uye.rol)} · {belgeSayisiText(lang, kisiselBelgeler.length)}</div>
        </div>
        {(canUpload && (kendiMi || yoneticiMi)) && (
          <button className="belgeYukleHizliBtn" style={{borderColor:renk,color:renk,background:renk+"18"}}
            onClick={()=>setYukleAcik(!yukleAcik)}>
            {yukleAcik ? "−" : `+ ${t("documents.upload")}`}
          </button>
        )}
      </div>

      {yukleAcik && (
        <BelgeYukleForm
          tip="kisisel"
          sahipUid={uye.uid}
          sahipAd={`${uye.ad} ${uye.soyad}`}
          uyeler={uyeler}
          user={user}
          belgeYukle={belgeYukle}
          yukleniyor={yukleniyor}
          yuklemePct={yuklemePct}
          onTamamlandi={()=>setYukleAcik(false)}
        />
      )}

      <BelgeListesi
        belgeler={kisiselBelgeler}
        user={user} uyeler={uyeler} yoneticiMi={yoneticiMi}
        belgeGuncelle={belgeGuncelle} belgeSil={belgeSil}
        belgeVersiyonEkle={belgeVersiyonEkle}
        yukleniyor={yukleniyor} yuklemePct={yuklemePct}
        className="belgeDetayListePanel"
      />
    </div>
  );
}

/* ─────────────────────────────────────────
   ANA BELGELER SAYFASI (Giriş Ekranı)
───────────────────────────────────────── */
function DocumentsPage({
  belgeler = [], uyeler = [],
  belgeYukle, belgeGuncelle, belgeSil, belgeVersiyonEkle,
  yukleniyor, yuklemePct,
  permissions, user, yoneticiMi,
  onDocumentsDetailStateChange,
  onDocumentsDetailBackRequest,
  isPremium = false, premiumConfig = {}, onPremiumLimitHit, onInternalForwardTransition,
}) {
  const { t, lang } = useLanguage();
  const limits = premiumConfig?.limits || {};

  // wrap belgeYukle with limit check
  const toplamBelgeMB = useMemo(() => {
    const toplamByte = belgeler.reduce((t, b) => t + Number(b.dosyaBoyutu || 0), 0);
    return toplamByte / (1024 * 1024);
  }, [belgeler]);

  const belgeYukleWithLimit = async (params) => {
    if (!isPremium) {
      const belgeSayisi = belgeler.length;
      const maxBelge = limits.belgeSayisi ?? 5;
      if (belgeSayisi + 1 > maxBelge) {
        onPremiumLimitHit && onPremiumLimitHit(dmt(lang, "Belgeler"), belgeSayisi, maxBelge, [dmt(lang, "Sınırsız belge yükleme"), dmt(lang, "5 GB depolama alanı"), dmt(lang, "Kişisel ve ortak belgeler"), dmt(lang, "Geçerlilik tarihi takibi")]);
        return false;
      }

      const maxDepoMB = Number(limits.belgeDepoMB ?? 30);
      const yeniDosyaMB = Number(params?.toplamDosyaBoyutu || params?.dosya?.size || 0) / (1024 * 1024);
      const yeniToplamMB = toplamBelgeMB + yeniDosyaMB;
      if (maxDepoMB > 0 && yeniToplamMB > maxDepoMB) {
        onPremiumLimitHit && onPremiumLimitHit(
          t("documents.premium.storageLimit"),
          Number(yeniToplamMB.toFixed(2)),
          maxDepoMB,
          [t("documents.premium.b1"), t("documents.premium.b2"), t("documents.premium.b3")],
          dmt(lang, "Belge deposu sınırın {{max}} MB. Bu dosya ile toplam kullanım {{total}} MB olacağı için yükleme engellendi.", { max: maxDepoMB, total: yeniToplamMB.toFixed(2) })
        );
        return false;
      }
    }
    return belgeYukle(params);
  };

  const belgeVersiyonEkleWithLimit = async ({ belgeId, eskiVeri, dosya }) => {
    if (!isPremium) {
      const maxDepoMB = Number(limits.belgeDepoMB ?? 30);
      const yeniDosyaMB = Number(dosya?.size || 0) / (1024 * 1024);
      const yeniToplamMB = toplamBelgeMB + yeniDosyaMB;
      if (maxDepoMB > 0 && yeniToplamMB > maxDepoMB) {
        onPremiumLimitHit && onPremiumLimitHit(
          t("documents.premium.storageLimit"),
          Number(yeniToplamMB.toFixed(2)),
          maxDepoMB,
          [t("documents.premium.b1"), t("documents.premium.b4")],
          dmt(lang, "Belge deposu sınırın {{max}} MB. Bu dosya ile toplam kullanım {{total}} MB olacağı için yükleme engellendi.", { max: maxDepoMB, total: yeniToplamMB.toFixed(2) })
        );
        return;
      }
    }
    return belgeVersiyonEkle({ belgeId, eskiVeri, dosya });
  };
  // ekran: null | "ortak" | { tip:"kisisel", uye }
  const [ekran, setEkran] = useState(null);
  const documentsOpenAnimatingRef = useRef(false);

  const canUpload = yoneticiMi || permissions?.actions?.addDocument === true;

  const detayAcik = ekran !== null;

  const documentsDetailBack = useMemo(() => {
    if (!detayAcik) return null;
    return () => {
      setEkran(null);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      });
    };
  }, [detayAcik]);

  useEffect(() => {
    if (detayAcik) {
      onDocumentsDetailStateChange?.(true);
    }
    // false durumu App.js back fonksiyonu tarafından rAF ile yönetilir (layout jump önlemek için)

    if (detayAcik && documentsDetailBack) {
      window.__documentsDetailCommitBack = documentsDetailBack;
    } else {
      if (window.__documentsDetailCommitBack === documentsDetailBack) delete window.__documentsDetailCommitBack;
    }

    return () => {
      if (window.__documentsDetailCommitBack === documentsDetailBack) delete window.__documentsDetailCommitBack;
    };
  }, [detayAcik, documentsDetailBack, onDocumentsDetailStateChange]);

  // Sayfa tamamen unmount olduğunda (navigateTo ile ayrılma) documentsDetailAcik sıfırlanır
  useEffect(() => {
    return () => {
      onDocumentsDetailStateChange?.(false);
    };
  }, [onDocumentsDetailStateChange]);

  const belgeDetayAc = (nextEkran) => {
    if (documentsOpenAnimatingRef.current) return;
    const commit = () => {
      window.scrollTo?.({ top: 0, behavior: "auto" });
      setEkran(nextEkran);
    };

    if (typeof onInternalForwardTransition === "function") {
      documentsOpenAnimatingRef.current = true;
      onInternalForwardTransition(commit);
      setTimeout(() => { documentsOpenAnimatingRef.current = false; }, 360);
      return;
    }

    commit();
  };

  // İstatistikler
  const ortakSayi  = belgeler.filter(b => b.tip === "ortak").length;
  const suruYakSayi = belgeler.filter(b => { const s=sureDurumu(b.gecerlilikTarihi, t); return s && s.gun >= 0 && s.gun <= 30; }).length;
  const suruDolSayi = belgeler.filter(b => { const s=sureDurumu(b.gecerlilikTarihi, t); return s && s.gun < 0; }).length;

  /* ── Alt sayfalara yönlendir ── */
  if (ekran === "ortak") {
    return <OrtakBelgelerSayfasi
      belgeler={belgeler} uyeler={uyeler} user={user}
      yoneticiMi={yoneticiMi} canUpload={canUpload}
      belgeYukle={belgeYukleWithLimit} belgeGuncelle={belgeGuncelle}
      belgeSil={belgeSil} belgeVersiyonEkle={belgeVersiyonEkleWithLimit}
      yukleniyor={yukleniyor} yuklemePct={yuklemePct}
      onGeri={onDocumentsDetailBackRequest || (()=>setEkran(null))}
    />;
  }

  if (ekran?.tip === "kisisel") {
    return <KisiselBelgelerSayfasi
      uye={ekran.uye} belgeler={belgeler} uyeler={uyeler}
      user={user} yoneticiMi={yoneticiMi} canUpload={canUpload}
      belgeYukle={belgeYukleWithLimit} belgeGuncelle={belgeGuncelle}
      belgeSil={belgeSil} belgeVersiyonEkle={belgeVersiyonEkleWithLimit}
      yukleniyor={yukleniyor} yuklemePct={yuklemePct}
      onGeri={onDocumentsDetailBackRequest || (()=>setEkran(null))}
    />;
  }

  /* ── Ana giriş ekranı ── */
  const siraliUyelerTemel = uyelerSirala(uyeler);
  const siraliUyeler = [
    ...siraliUyelerTemel.filter(u => u.uid === user?.uid),
    ...siraliUyelerTemel.filter(u => u.uid !== user?.uid),
  ];

  const belgeKartaErisebilirMi = (u) => {
    if (u.uid === user?.uid) return true;
    if (yoneticiMi) return true;
    return belgeler.some(b =>
      b.tip === "kisisel" &&
      b.sahipUid === u.uid &&
      (b.yukleyenUid === user?.uid || (b.gorebilecekler && b.gorebilecekler.includes(user?.uid)))
    );
  };
  const uyeUidSet = new Set(uyeler.map((u) => u.uid));
  const manuelProfilKartlari = Object.values(belgeler.reduce((acc, b) => {
    if (b.tip !== "kisisel" || !b.sahipUid || uyeUidSet.has(b.sahipUid)) return acc;
    const evcilMi = String(b.kategori || "").includes("Evcil");
    const bebekMi = String(b.kategori || "").includes("Çocuk");
    if (!acc[b.sahipUid]) {
      const adParcalari = String(b.sahipAd || dmt(lang, "Manuel Profil")).trim().split(" ");
      acc[b.sahipUid] = {
        uid: b.sahipUid,
        ad: adParcalari[0] || dmt(lang, "Profil"),
        soyad: adParcalari.slice(1).join(" "),
        rol: evcilMi ? "Evcil Hayvan" : bebekMi ? "Çocuk/Bebek" : "Profil",
        avatar: getDocumentProfileAvatar(b),
        sahipAvatar: b.sahipAvatar || null,
        sahipProfileType: b.sahipProfileType || (evcilMi ? "pet" : bebekMi ? "baby" : "child"),
        calendarColor: evcilMi ? "#22c55e" : bebekMi ? "#f59e0b" : "#60a5fa",
        manualDocumentProfile: true,
      };
    }
    return acc;
  }, {}));

  return (
    <div className="pageContent belgeSabitSayfa belgeAnaSayfa">

      {/* İstatistik bant */}
      <div className="statsBand" style={{marginBottom:"16px"}}>
        <div className="statsBandItem">
          <span className="statsBandNum">{belgeler.length}</span>
          <span className="statsBandLbl">{dmt(lang, "Toplam")}</span>
        </div>
        <div className="statsBandDivider"/>
        <div className="statsBandItem">
          <span className="statsBandNum yellow">{suruYakSayi}</span>
          <span className="statsBandLbl">{dmt(lang, "Süresi Yakın")}</span>
        </div>
        <div className="statsBandDivider"/>
        <div className="statsBandItem">
          <span className="statsBandNum red">{suruDolSayi}</span>
          <span className="statsBandLbl">{dmt(lang, "Süresi Dolmuş")}</span>
        </div>
      </div>

      {/* ── ORTAK BELGELER KARTI ── */}
      <button className="belgeAnaSayfaKarti ortakKart" onClick={()=>belgeDetayAc("ortak")}>
        <div className="belgeAnaSayfaKartiIkon">📁</div>
        <div className="belgeAnaSayfaKartiIcerik">
          <div className="belgeAnaSayfaKartiBaslik">{dmt(lang, "Ailenin Ortak Belgeleri")}</div>
          <div className="belgeAnaSayfaKartiAlt">{dmt(lang, "Tüm ailenin erişebildiği belgeler")}</div>
          <div className="belgeAnaSayfaKartiSayi">{belgeSayisiText(lang, ortakSayi)}</div>
        </div>
        <span className="belgeAnaSayfaKartiChevron">›</span>
      </button>

      {/* ── AİLE ÜYELERİ ── */}
      <div className="belgeKartlariScrollAlani">
      <div className="belgeAnaSayfaBaslik">{dmt(lang, "Aile Üyeleri")}</div>

      <div className="belgeUyeGrid">
        {siraliUyeler.map(u => {
          const renk = u.calendarColor || "#2563eb";
          const erisebilir = belgeKartaErisebilirMi(u);
          const kisiselSayi = belgeler.filter(b => {
            if (b.tip !== "kisisel" || b.sahipUid !== u.uid) return false;
            if (yoneticiMi || b.sahipUid === user?.uid || b.yukleyenUid === user?.uid) return true;
            return b.gorebilecekler && b.gorebilecekler.includes(user?.uid);
          }).length;

          const suruYakUye = belgeler.filter(b => {
            if (b.tip !== "kisisel" || b.sahipUid !== u.uid) return false;
            if (!yoneticiMi && b.sahipUid !== user?.uid && b.yukleyenUid !== user?.uid &&
                (!b.gorebilecekler || !b.gorebilecekler.includes(user?.uid))) return false;
            const s = sureDurumu(b.gecerlilikTarihi, t);
            return s && s.gun >= 0 && s.gun <= 30;
          }).length;

          const kendiMi = u.uid === user?.uid;

          return (
            <button key={u.uid} className="belgeUyeKarti"
              style={{borderColor: renk+"44", background: renk+"0d", opacity: erisebilir ? 1 : 0.45, cursor: erisebilir ? "pointer" : "not-allowed"}}
              onClick={() => { if (erisebilir) belgeDetayAc({tip:"kisisel", uye:u}); }}>
              <div className="belgeUyeKartiAvatar" style={{ overflow: "hidden" }}>
                <AvatarImg
                  avatar={u.avatar || null}
                  renk={renk}
                  initials={`${u.ad?.[0]||""}${u.soyad?.[0]||""}`}
                  size={50}
                  glow={false}
                  radius="50%"
                
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
              </div>
              <div className="belgeUyeKartiAd">{u.ad}{kendiMi && <span className="belgeUyeKartiSen"> ({dmt(lang, "Ben")})</span>}</div>
              <div className="belgeUyeKartiRol">{rolLabel(lang, u.rol)}</div>
              <div className="belgeUyeKartiSayi" style={{color:renk}}>{belgeSayisiText(lang, kisiselSayi)}</div>
              {suruYakUye > 0 && (
                <div className="belgeUyeKartiUyari">⚠️ {suruYakUye}</div>
              )}
            </button>
          );
        })}
      </div>

      {manuelProfilKartlari.length > 0 && (
        <>
          <div className="belgeAnaSayfaBaslik" style={{marginTop:"16px"}}>{dmt(lang, "Çocuk / Bebek / Evcil Profilleri")}</div>
          <div className="belgeUyeGrid">
            {manuelProfilKartlari.map((u) => {
              const renk = u.calendarColor || "#f59e0b";
              const kisiselSayi = belgeler.filter((b) => b.tip === "kisisel" && b.sahipUid === u.uid).length;
              return (
                <button key={u.uid} className="belgeUyeKarti"
                  style={{borderColor: renk+"44", background: renk+"0d"}}
                  onClick={()=>belgeDetayAc({tip:"kisisel", uye:u})}>
                  <div className="belgeUyeKartiAvatar" style={{ overflow: "hidden" }}>
                    <AvatarImg
                      avatar={getDocumentProfileAvatar(u)}
                      renk={renk}
                      initials={ROL_IKONLARI[u.rol] || `${u.ad?.[0]||""}`}
                      size={50}
                      glow={false}
                      radius="50%"
                    />
                  </div>
                  <div className="belgeUyeKartiAd">{u.ad} {u.soyad}</div>
                  <div className="belgeUyeKartiRol">{rolLabel(lang, u.rol)}</div>
                  <div className="belgeUyeKartiSayi" style={{color:renk}}>{belgeSayisiText(lang, kisiselSayi)}</div>
                </button>
              );
            })}
          </div>
        </>
      )}
      </div>

    </div>
  );
}

export default DocumentsPage;
